/**
 * OCR読取項目の検索キーワードを扱うユーティリティ（#3204）。
 *
 * 選択肢項目（ラジオボタン・プルダウン）は複数選択で検索できるため、
 * 1項目あたりの検索値はテキスト項目の string と選択肢項目の string[] が混在する。
 * 画面遷移（location.state）経由で旧形式（string のみ）が流れ込むこともあるため、
 * 読み出し側は必ずここのヘルパーを通して正規化する。
 */

/** 1項目あたりの検索値。テキスト・日付は string、選択肢項目は string[] */
export type OcrItemKeywordValue = string | string[];

/** fieldName → 検索値 のマップ */
export type OcrItemKeywords = Record<string, OcrItemKeywordValue>;

/**
 * 検索値を文字列配列に正規化する。
 * 空文字・空白のみの値は検索条件として意味を持たないため除去する。
 */
export const toKeywordList = (value: OcrItemKeywordValue | undefined): string[] => {
  if (value === undefined || value === null) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map(v => v.trim()).filter(v => v !== '');
};

/**
 * 検索値を単一文字列として取り出す（TextField・Datepicker 用）。
 * 配列が入っていた場合は先頭要素を採用する。
 */
export const toKeywordText = (value: OcrItemKeywordValue | undefined): string => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value[0] ?? '';
  return value;
};

/**
 * location.state 等の外部由来の値を OcrItemKeywords に正規化する。
 * 想定外の型（数値・null など）は検索条件から落とす。
 */
export const normalizeOcrItemKeywords = (input: unknown): OcrItemKeywords => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const normalized: OcrItemKeywords = {};
  for (const [fieldName, value] of Object.entries(input as Record<string, unknown>)) {
    if (typeof value === 'string') {
      normalized[fieldName] = value;
    } else if (Array.isArray(value)) {
      const strings = value.filter((v): v is string => typeof v === 'string');
      if (strings.length > 0) normalized[fieldName] = strings;
    }
  }
  return normalized;
};
